/**
 * Tokenizer for Feynman DSL.
 * Converts source text into a stream of tokens for parsing.
 */

import { ParseError } from './types';

/**
 * Token types recognized by the tokenizer.
 */
export type TokenType =
  | 'ANIMATE_SEQUENCE'
  | 'PARTICIPANT'
  | 'STEP'
  | 'END'
  | 'FOCUS'
  | 'DIM'
  | 'SHOW'
  | 'HIDE'
  | 'ACTIVATE'
  | 'DEACTIVATE'
  | 'NOTE'
  | 'OVER'
  | 'LEFT'
  | 'RIGHT'
  | 'FRAGMENT'
  | 'ALT'
  | 'OPT'
  | 'LOOP'
  | 'PAR'
  | 'STRING'
  | 'IDENTIFIER'
  | 'NUMBER'
  | 'ARROW'
  | 'COLON'
  | 'COMMA'
  | 'LPAREN' // (
  | 'RPAREN' // )
  | 'EQUALS' // =
  | 'AMPERSAND' // & for anchor definitions
  | 'ASTERISK' // * for anchor references
  | 'NEWLINE'
  | 'EOF';

/**
 * A token with its type, value, and location in the source.
 */
export interface Token {
  type: TokenType;
  value: string;
  line: number;
  column: number;
}

/**
 * Keywords in the Feynman DSL.
 */
const KEYWORDS: Record<string, TokenType> = {
  animateSequence: 'ANIMATE_SEQUENCE',
  participant: 'PARTICIPANT',
  step: 'STEP',
  end: 'END',
  focus: 'FOCUS',
  dim: 'DIM',
  show: 'SHOW',
  hide: 'HIDE',
  activate: 'ACTIVATE',
  deactivate: 'DEACTIVATE',
  note: 'NOTE',
  over: 'OVER',
  left: 'LEFT',
  right: 'RIGHT',
  fragment: 'FRAGMENT',
  alt: 'ALT',
  opt: 'OPT',
  loop: 'LOOP',
  par: 'PAR',
};

/**
 * Tokenizer class that converts source code into tokens.
 */
export class Tokenizer {
  private pos = 0;
  private line = 1;
  private column = 1;
  private source: string;

  constructor(source: string) {
    this.source = source;
  }

  /**
   * Tokenizes the entire source and returns an array of tokens.
   *
   * @returns Array of tokens including EOF token at the end
   * @throws {ParseError} When encountering invalid syntax
   */
  public tokenize(): Token[] {
    const tokens: Token[] = [];

    while (this.pos < this.source.length) {
      const token = this.nextToken();
      if (token !== null) {
        tokens.push(token);
      }
    }

    // Add EOF token
    tokens.push({
      type: 'EOF',
      value: '',
      line: this.line,
      column: this.column,
    });

    return tokens;
  }

  /**
   * Gets the next token from the source.
   *
   * @returns Next token or null if whitespace was consumed
   */
  private nextToken(): Token | null {
    this.skipWhitespace();

    if (this.pos >= this.source.length) {
      return null;
    }

    const char = this.source[this.pos];
    if (char === undefined) {
      return null;
    }

    const line = this.line;
    const column = this.column;

    // Newline
    if (char === '\n') {
      this.advance();
      this.line++;
      this.column = 1;
      return { type: 'NEWLINE', value: '\n', line, column };
    }

    // String literals
    if (char === '"') {
      return this.readString(line, column);
    }

    // Arrows (multi-character)
    if (char === '-') {
      return this.readArrow(line, column);
    }

    // Colon
    if (char === ':') {
      this.advance();
      return { type: 'COLON', value: ':', line, column };
    }

    // Comma
    if (char === ',') {
      this.advance();
      return { type: 'COMMA', value: ',', line, column };
    }

    // Ampersand (anchor definition)
    if (char === '&') {
      this.advance();
      return { type: 'AMPERSAND', value: '&', line, column };
    }

    // Asterisk (anchor reference)
    if (char === '*') {
      this.advance();
      return { type: 'ASTERISK', value: '*', line, column };
    }

    // Left parenthesis
    if (char === '(') {
      this.advance();
      return { type: 'LPAREN', value: '(', line, column };
    }

    // Right parenthesis
    if (char === ')') {
      this.advance();
      return { type: 'RPAREN', value: ')', line, column };
    }

    // Equals sign
    if (char === '=') {
      this.advance();
      return { type: 'EQUALS', value: '=', line, column };
    }

    // Numbers (integers and decimals)
    if (this.isDigit(char)) {
      return this.readNumber(line, column);
    }

    // Identifiers and keywords
    if (this.isAlpha(char)) {
      return this.readIdentifier(line, column);
    }

    // Color hex values or comments
    if (char === '#') {
      // Check if this is a hex color (#rrggbb) or a comment
      const nextChar =
        this.pos + 1 < this.source.length ? this.source[this.pos + 1] : null;
      if (nextChar && this.isHexDigit(nextChar)) {
        // This is a color value
        return this.readColorHex(line, column);
      } else {
        // This is a comment
        this.skipComment();
        return this.nextToken();
      }
    }

    throw new ParseError(`Unexpected character: '${char}'`, line, column);
  }

  /**
   * Reads a string literal enclosed in double quotes.
   */
  private readString(line: number, column: number): Token {
    this.advance(); // Skip opening quote
    let value = '';

    while (this.pos < this.source.length) {
      const char = this.source[this.pos];
      if (char === undefined) {
        break;
      }
      if (char === '"') {
        break;
      }
      if (char === '\n') {
        throw new ParseError('Unterminated string literal', line, column);
      }
      value += char;
      this.advance();
    }

    if (this.pos >= this.source.length) {
      throw new ParseError('Unterminated string literal', line, column);
    }

    this.advance(); // Skip closing quote
    return { type: 'STRING', value, line, column };
  }

  /**
   * Reads an arrow token (-> | --> | ->> | -->>).
   */
  private readArrow(line: number, column: number): Token {
    let value = '';

    // Read '-' or '--'
    value += this.source[this.pos];
    this.advance();

    if (this.pos < this.source.length && this.source[this.pos] === '-') {
      value += this.source[this.pos];
      this.advance();
    }

    // Read '>' or '>>'
    if (this.pos < this.source.length && this.source[this.pos] === '>') {
      value += this.source[this.pos];
      this.advance();

      if (this.pos < this.source.length && this.source[this.pos] === '>') {
        value += this.source[this.pos];
        this.advance();
      }
    } else {
      throw new ParseError(`Invalid arrow syntax: '${value}'`, line, column);
    }

    return { type: 'ARROW', value, line, column };
  }

  /**
   * Reads an identifier or keyword.
   */
  private readIdentifier(line: number, column: number): Token {
    let value = '';

    while (this.pos < this.source.length) {
      const char = this.source[this.pos];
      if (char === undefined) {
        break;
      }
      if (!this.isAlpha(char) && !this.isDigit(char) && char !== '_') {
        break;
      }
      value += char;
      this.advance();
    }

    const type = KEYWORDS[value] ?? 'IDENTIFIER';
    return { type, value, line, column };
  }

  /**
   * Reads a number (integer or decimal) optionally followed by a unit (s, ms).
   * Examples: 1, 1.5, 500ms, 2.5s
   */
  private readNumber(line: number, column: number): Token {
    let value = '';

    // Read integer part
    while (this.pos < this.source.length) {
      const char = this.source[this.pos];
      if (!char || !this.isDigit(char)) break;
      value += char;
      this.advance();
    }

    // Read decimal part if present
    if (this.pos < this.source.length && this.source[this.pos] === '.') {
      const nextChar = this.source[this.pos + 1];
      if (nextChar && this.isDigit(nextChar)) {
        value += '.';
        this.advance();

        while (this.pos < this.source.length) {
          const char = this.source[this.pos];
          if (!char || !this.isDigit(char)) break;
          value += char;
          this.advance();
        }
      }
    }

    // Check for unit suffix (ms or s)
    if (this.pos < this.source.length) {
      const char = this.source[this.pos];
      if (char && this.isAlpha(char)) {
        // Read unit
        let unit = '';
        while (this.pos < this.source.length) {
          const unitChar = this.source[this.pos];
          if (!unitChar || !this.isAlpha(unitChar)) break;
          unit += unitChar;
          this.advance();
        }

        if (unit === 'ms' || unit === 's') {
          value += unit;
        } else {
          // Invalid unit, treat as separate identifier (will cause parse error)
          // Rewind to before the unit
          for (let i = 0; i < unit.length; i++) {
            this.pos--;
            this.column--;
          }
        }
      }
    }

    return { type: 'NUMBER', value, line, column };
  }

  /**
   * Reads a hex color value (#rrggbb or #rgb).
   * Examples: #ff6b6b, #f00, #0088ff
   */
  private readColorHex(line: number, column: number): Token {
    let value = '';

    // Read the '#'
    const hashChar = this.source[this.pos];
    if (hashChar) {
      value += hashChar;
    }
    this.advance();

    // Read hex digits (3 or 6 characters typical, but we'll be flexible)
    while (this.pos < this.source.length) {
      const char = this.source[this.pos];
      if (!char || !this.isHexDigit(char)) break;
      value += char;
      this.advance();
    }

    // Validate length (should be 3 or 6 hex digits after #)
    const hexPart = value.slice(1);
    if (hexPart.length !== 3 && hexPart.length !== 6) {
      throw new ParseError(
        `Invalid color hex format: '${value}'. Expected #rgb or #rrggbb`,
        line,
        column
      );
    }

    return { type: 'IDENTIFIER', value, line, column };
  }

  /**
   * Skips whitespace characters (except newlines).
   */
  private skipWhitespace(): void {
    while (
      this.pos < this.source.length &&
      (this.source[this.pos] === ' ' ||
        this.source[this.pos] === '\t' ||
        this.source[this.pos] === '\r')
    ) {
      this.advance();
    }
  }

  /**
   * Skips comment lines starting with #.
   */
  private skipComment(): void {
    while (this.pos < this.source.length && this.source[this.pos] !== '\n') {
      this.advance();
    }
  }

  /**
   * Advances the position by one character.
   */
  private advance(): void {
    this.pos++;
    this.column++;
  }

  /**
   * Checks if a character is alphabetic.
   */
  private isAlpha(char: string): boolean {
    return /[a-zA-Z]/.test(char);
  }

  /**
   * Checks if a character is a digit.
   */
  private isDigit(char: string): boolean {
    return /[0-9]/.test(char);
  }

  /**
   * Checks if a character is a hexadecimal digit.
   */
  private isHexDigit(char: string): boolean {
    return /[0-9a-fA-F]/.test(char);
  }
}
